import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { askIQ, askIQStream } from "../../../account/src/api/askIQApi";
import { normalizeMentions } from "../../AskAQuestionPrompt/mentionTokens";
import { describeAskAiConversationFailure } from "../../../account/src/api/askAiConversationApi";
import store from "../../../account/src/store";

export const useIQChatStore = defineStore("iqChat", () => {
  // State
  const messages = ref([]);
  const isLoading = ref(false);
  const isGenerating = ref(false);
  const isThinking = ref(false); // New: tracks when AI is in "thinking" phase
  /** True from get_suggestions/ request start until SSE complete or error. */
  const isSuggestionStreamActive = ref(false);
  const currentReasoning = ref(""); // New: stores the reasoning/thinking text
  const currentResponse = ref("");
  let abortController = null;

  // Getters
  const hasMessages = computed(() => messages.value.length > 0);
  const lastMessage = computed(() => messages.value[messages.value.length - 1]);

  // Actions
  const addUserMessage = (message, options = {}) => {
    const id = Date.now();
    const entry = {
      id,
      type: "user",
      content: message,
      format: "text",
      timestamp: new Date(),
    };
    if (Array.isArray(options.attachmentRefs) && options.attachmentRefs.length > 0) {
      entry.attachmentRefs = options.attachmentRefs;
    }
    const mentions = normalizeMentions(options.mentions);
    if (mentions.length > 0) {
      entry.mentions = mentions;
    }
    messages.value.push(entry);
    return id;
  };

  const rollbackUserMessage = (messageId) => {
    if (messageId == null) {
      return;
    }
    messages.value = messages.value.filter((message) => message.id !== messageId);
  };

  const handleAskAiConversationFailure = (error, rollbackUserMessageId = null) => {
    rollbackUserMessage(rollbackUserMessageId);
    addAIMessage(describeAskAiConversationFailure(error), { isError: true });
  };

  const addAIMessage = (message, options = {}) => {
    const isError = options.isError === true;
    messages.value.push({
      id: isError ? `error-${Date.now()}` : Date.now(),
      type: "ai",
      content: message,
      format: "markdown",
      timestamp: new Date(),
      ...(isError ? { isError: true } : {}),
    });
  };

  const startGenerating = () => {
    isGenerating.value = true;
    isLoading.value = true;
    currentResponse.value = "";
    currentReasoning.value = "";
  };

  const startThinking = () => {
    isThinking.value = true;
    currentReasoning.value = "";
  };

  const stopThinking = () => {
    isThinking.value = false;
  };

  const updateCurrentReasoning = (reasoning) => {
    currentReasoning.value = reasoning;
  };

  const stopGenerating = () => {
    // Cancel any ongoing API request
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    // Add AI message BEFORE setting isGenerating to false
    // This ensures watchers see the message when they check for suggestions
    if (currentResponse.value) {
      addAIMessage(currentResponse.value);
      currentResponse.value = "";
    }

    isGenerating.value = false;
    isLoading.value = false;
    isThinking.value = false;
    isSuggestionStreamActive.value = false;
    currentReasoning.value = "";
    // If stopped during loading (no response yet), question remains without answer
  };

  /**
   * ER-12337: finish a generation that owns an external AbortController (background
   * video-microlearning runs). Mirrors stopGenerating's message flush + state reset
   * but never touches the shared controller — that belongs to whichever foreground
   * IQ surface currently owns it, so aborting it here would cancel unrelated work.
   */
  const finishExternalGeneration = () => {
    if (currentResponse.value) {
      addAIMessage(currentResponse.value);
      currentResponse.value = "";
    }
    isGenerating.value = false;
    isLoading.value = false;
    isThinking.value = false;
    isSuggestionStreamActive.value = false;
    currentReasoning.value = "";
  };

  const updateCurrentResponse = (response) => {
    currentResponse.value = response;
  };

  const clearChat = () => {
    // Cancel any ongoing API request
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    messages.value = [];
    currentResponse.value = "";
    currentReasoning.value = "";
    isGenerating.value = false;
    isLoading.value = false;
    isThinking.value = false;
    isSuggestionStreamActive.value = false;
  };

  /** Replace thread (e.g. after loading a saved conversation). */
  const setMessages = (list) => {
    messages.value = Array.isArray(list) ? list : [];
  };

  const generateRandomResponse = async (userMessage) => {
    // Start with loading state (thinking period)
    isLoading.value = true;
    isGenerating.value = false;
    currentResponse.value = "";

    // Wait 2-3 seconds before starting to type (thinking time)
    await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 1000));

    if (!isLoading.value) return; // Stop if user clicked stop during thinking

    // Switch to generating state
    isLoading.value = false;
    isGenerating.value = true;

    // Simulate AI thinking and generating response with longer content
    const responses = [
      "I understand your question about that topic. Let me help you with some comprehensive insights that should clarify things for you. Based on my analysis, there are several key points to consider. First, the foundational concepts are crucial to understand. Second, the practical applications can vary significantly. Third, the long-term implications should not be overlooked. Let me break this down further for you.",
      "That's an interesting point you've raised. Based on my analysis, I would suggest a multi-faceted approach to this problem. The solution involves understanding the core principles, examining the current landscape, and identifying potential opportunities. There are several strategies we can explore together. Each approach has its own merits and considerations that we should discuss in detail.",
      "I can see you're looking for information on this subject. Here's what I can tell you based on my knowledge and experience. This is a complex topic that requires careful consideration of multiple factors. The key is to understand the underlying mechanisms and how they interact with each other. There are several approaches we can take, each with its own advantages and potential challenges.",
      "Great question! This is a complex topic, but I'll break it down for you in a way that's easy to understand. The fundamental concepts are important to grasp first. Then we can explore the practical applications and real-world implications. There are several key areas we should focus on, and each one contributes to the overall understanding. Let me provide you with a comprehensive overview.",
      "I appreciate you asking about this. Let me provide you with a comprehensive answer that covers the essential aspects. This topic involves multiple interconnected elements that work together to create the overall system. Understanding these relationships is crucial for making informed decisions. There are several perspectives to consider, and each offers valuable insights into the bigger picture.",
      "That's a thoughtful question. From my perspective, here are the key points that should be addressed. The first consideration is the foundational understanding of the concepts involved. Second, we need to examine the practical implications and real-world applications. Third, it's important to consider the long-term effects and potential outcomes. Each of these aspects contributes to a complete understanding of the topic.",
      "I'm glad you brought this up. This is something that many people wonder about, and it's a great question to explore. The answer involves several interconnected factors that work together in complex ways. Understanding these relationships is key to grasping the full picture. There are multiple approaches we can take to address this, and each offers unique insights into the topic at hand.",
      "Excellent question! Let me share some insights that might be helpful for your understanding. This is a multifaceted topic that requires us to consider various angles and perspectives. The key is to understand how different elements interact and influence each other. There are several important factors to keep in mind, and each one plays a crucial role in the overall system.",
    ];

    const randomResponse = responses[Math.floor(Math.random() * responses.length)];

    // Simulate typing effect (ChatGPT-like behavior - word by word)
    const words = randomResponse.split(" ");
    let typedResponse = "";

    for (let i = 0; i < words.length; i++) {
      if (!isGenerating.value) break; // Stop if user clicked stop

      // Add word with space (except for the last word)
      typedResponse += words[i] + (i < words.length - 1 ? " " : "");

      // During typing, only show the answer text (no sources for random responses)
      updateCurrentResponse({
        answer: typedResponse,
        sources: [],
        version: "1.0.0",
      });

      // ChatGPT-like speed: 3-4 words at a time with variable delay
      const wordsPerBatch = Math.random() < 0.3 ? 4 : 3; // 30% chance for 4 words, 70% for 3 words
      if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 50 + Math.random() * 100));
      }
    }

    stopGenerating();
  };

  // Fallback reasoning templates for when streaming is not available
  const generateFallbackReasoning = () => {
    const templates = [
      `### Analyzing Your Query\n\nI'm examining the key aspects of your question to provide the most relevant and accurate response.\n\n**Understanding Context:**\n* Identifying the main subject of your inquiry\n* Considering related concepts and dependencies\n* Evaluating the scope of information needed`,

      `### Reasoning Process\n\n**Step 1: Query Analysis**\nBreaking down your question to understand the core requirements and context.\n\n**Step 2: Information Gathering**\nRetrieving relevant data points and connecting related concepts.`,

      `### Thought Process\n\n**Initial Assessment:**\nEvaluating the nature of your request and determining the best approach.\n\n**Key Considerations:**\n* Relevance to your current context\n* Accuracy of information`,
    ];
    return templates[Math.floor(Math.random() * templates.length)];
  };

  const parseMaybeJson = (value) => {
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const normalizeCompetenceCategory = (category) => {
    const normalized = (category || "ability").toLowerCase();
    return normalized === "behavior" ? "behaviour" : normalized;
  };

  const mapInstructionStep = (step = {}) => ({
    competence_category: normalizeCompetenceCategory(
      step.competence_category || step.competency_category
    ),
    inspector_action: step.inspector_action || step.action || step.chapter_title || "",
    look_for: step.look_for || step.chapter_description || "",
    pass_criteria: step.pass_criteria || step.chapter_summary || "",
    fail_criteria: step.fail_criteria || "",
    timestamp_ref:
      step.timestamp_ref ||
      (step.start_time && step.end_time ? `${step.start_time} - ${step.end_time}` : ""),
    video_jump_url: step.video_jump_url || "",
  });

  const normalizeVideoInstructionResponse = (rawResponse) => {
    const parsedRoot = parseMaybeJson(rawResponse) || {};
    const parsedData =
      parsedRoot && typeof parsedRoot === "object" && parsedRoot.data != null
        ? parseMaybeJson(parsedRoot.data)
        : parsedRoot;
    const payload = parsedData && typeof parsedData === "object" ? parsedData : {};
    const videoUrl = payload.video_url || payload.video || null;
    const validation = payload.inspection_instructions?.validation || payload.validation || null;

    const legacySteps = Array.isArray(payload.instructions?.steps)
      ? payload.instructions.steps.map(mapInstructionStep)
      : [];
    if (legacySteps.length > 0) {
      return {
        type: "pass",
        suggestions: {
          steps: legacySteps,
          summary: payload.instructions?.overall_rubric || null,
          error: null,
          video_url: videoUrl,
          video_title: payload.instructions?.video_title || null,
          validation,
        },
        answer:
          payload.message ||
          `I analyzed the video and created ${legacySteps.length} inspection ${legacySteps.length === 1 ? "step" : "steps"}. Review them in the right pane.`,
      };
    }

    if (validation?.status === "FAIL") {
      return {
        type: "fail",
        suggestions: {
          steps: [],
          summary: null,
          error: validation.reasoning || "Video validation failed.",
          video_url: videoUrl,
          validation,
        },
        answer: payload.message || "This video does not match the context of the checkpoint!",
      };
    }

    /**
     * MDP-452 / video-to-components PASS: keep quiz, TOC chapters, and inspection protocol on suggestions.
     * API may use `table_of_contents.chapters` (not `chapters`) and `inspection_instructions.steps`
     * (not `inspection_instructions.instructions.steps`). Validation often lives on `data.validation` only.
     */
    const inspectionInstructionsBlock = payload.inspection_instructions;
    const tripleValidationForPass = inspectionInstructionsBlock?.validation ?? validation ?? null;
    const passStatus =
      typeof tripleValidationForPass?.status === "string"
        ? tripleValidationForPass.status.toUpperCase()
        : "";

    const tocChapters = Array.isArray(payload.table_of_contents?.chapters)
      ? payload.table_of_contents.chapters
      : [];
    const rootChapters = Array.isArray(payload.chapters) ? payload.chapters : [];
    const chapterSource = rootChapters.length > 0 ? rootChapters : tocChapters;

    const quizPresent =
      payload.quiz != null && typeof payload.quiz === "object" && !Array.isArray(payload.quiz);
    const inspectionPresent =
      inspectionInstructionsBlock != null && typeof inspectionInstructionsBlock === "object";

    const instrNested = inspectionInstructionsBlock?.instructions;
    const protocolStepsRaw = Array.isArray(instrNested?.steps)
      ? instrNested.steps
      : Array.isArray(inspectionInstructionsBlock?.steps)
        ? inspectionInstructionsBlock.steps
        : [];

    const rawInstrTriple =
      instrNested && typeof instrNested === "object" && Array.isArray(instrNested.steps)
        ? instrNested
        : inspectionInstructionsBlock &&
            typeof inspectionInstructionsBlock === "object" &&
            Array.isArray(inspectionInstructionsBlock.steps)
          ? inspectionInstructionsBlock
          : null;

    const isRichPassPayload =
      passStatus === "PASS" &&
      quizPresent &&
      inspectionPresent &&
      chapterSource.length > 0 &&
      protocolStepsRaw.length > 0;

    if (isRichPassPayload) {
      const chapterStepsMapped = chapterSource.map(mapInstructionStep);
      const protocolStepsMapped = protocolStepsRaw.map(mapInstructionStep);
      const quizQuestionsNormalized = Array.isArray(payload.quiz)
        ? payload.quiz
        : Array.isArray(payload.quiz?.questions)
          ? payload.quiz.questions
          : [];

      const quizForSuggestions =
        payload.quiz != null &&
        typeof payload.quiz === "object" &&
        !Array.isArray(payload.quiz) &&
        Array.isArray(payload.quiz.questions)
          ? payload.quiz
          : quizQuestionsNormalized;

      const mlSummaryRaw =
        payload.microlearning_summary ??
        payload.chapters_summary ??
        payload.table_of_contents?.summary ??
        payload.table_of_contents?.videoSummary ??
        null;
      let microlearning_summary = null;
      if (typeof mlSummaryRaw === "string" && mlSummaryRaw.trim()) {
        microlearning_summary = { overview: mlSummaryRaw.trim() };
      } else if (mlSummaryRaw && typeof mlSummaryRaw === "object") {
        microlearning_summary = mlSummaryRaw;
      }

      return {
        type: "pass",
        suggestions: {
          chapters: chapterStepsMapped,
          table_of_contents: payload.table_of_contents ?? null,
          quiz: quizForSuggestions,
          inspection_instructions: inspectionInstructionsBlock,
          microlearning_summary,
          steps: protocolStepsMapped.length ? protocolStepsMapped : chapterStepsMapped,
          summary: rawInstrTriple?.overall_rubric ?? null,
          error: null,
          video_url: videoUrl,
          video_title:
            rawInstrTriple?.video_title ?? inspectionInstructionsBlock?.video_title ?? null,
          validation: tripleValidationForPass,
        },
        answer:
          payload.message ||
          "Review generated microlearning chapters, quiz, and inspection protocol.",
      };
    }

    const rawInstructions =
      payload.inspection_instructions?.instructions || payload.instructions || null;
    const mappedInstructionSteps = Array.isArray(rawInstructions?.steps)
      ? rawInstructions.steps.map(mapInstructionStep)
      : Array.isArray(rawInstructions)
        ? rawInstructions.map(mapInstructionStep)
        : [];
    const chapterSteps = Array.isArray(payload.table_of_contents?.chapters)
      ? payload.table_of_contents.chapters.map(mapInstructionStep)
      : [];
    const steps = mappedInstructionSteps.length ? mappedInstructionSteps : chapterSteps;

    if (steps.length > 0 || rawInstructions?.overall_rubric) {
      return {
        type: "pass",
        suggestions: {
          steps,
          summary: rawInstructions?.overall_rubric || null,
          error: null,
          video_url: videoUrl,
          video_title: rawInstructions?.video_title || null,
          validation,
        },
        answer:
          payload.message ||
          `I analyzed the video and created ${steps.length} inspection ${steps.length === 1 ? "step" : "steps"}. Review them in the right pane.`,
      };
    }

    return {
      type: "none",
      suggestions: null,
      answer: payload.error || payload.answer || payload.response || null,
    };
  };

  const isIdeaMDP452Enabled = () => {
    try {
      return !!store.getters["featureFlags/ideaMDP452"];
    } catch {
      return false;
    }
  };

  /**
   * Ask IQ with attached files through the IQ microservice ``/ask`` endpoint.
   *
   * The files are uploaded first (see ``IQSidePanelShell``); ``attachments``
   * are the storage references IQ fetches as multimodal input (IQ Chat Rule 12).
   * The answer streams back and renders with the same typing effect as a
   * text-only question. ``conversationId`` continues an IQ thread when one is
   * already known and is refreshed from the ``complete`` payload otherwise.
   */
  const generateAttachmentResponse = async ({
    question,
    attachments = [],
    conversationId = null,
    rollbackUserMessageId = null,
    onAfterAskSuccess = null,
    mentions = [],
  } = {}) => {
    try {
      isLoading.value = true;
      isGenerating.value = false;
      isThinking.value = true;
      currentResponse.value = "";
      currentReasoning.value = "";
      abortController = new AbortController();

      let fullResponse = null;
      let resolvedConversationId = conversationId;

      const captureConversationId = (data) => {
        if (data && typeof data === "object" && data.conversation_id) {
          resolvedConversationId = data.conversation_id;
        }
      };

      const response = await askIQStream({
        question,
        k: 5,
        signal: abortController.signal,
        conversationId,
        attachments,
        mentions,
        onThinkingUpdate: (thinkingText) => {
          if (isThinking.value) {
            currentReasoning.value = thinkingText;
          }
        },
        onComplete: (completeData) => {
          fullResponse = completeData;
          captureConversationId(completeData);
        },
      });

      if (!fullResponse && response?.data) {
        fullResponse = response.data;
        captureConversationId(fullResponse);
      }

      if (!isLoading.value) {
        return { conversationId: resolvedConversationId };
      }

      const reasoning = currentReasoning.value || fullResponse?.reasoning || "";
      await new Promise((resolve) => setTimeout(resolve, 300));

      isThinking.value = false;
      isLoading.value = false;
      isGenerating.value = true;

      const aiResponse =
        fullResponse?.answer ||
        fullResponse?.content ||
        fullResponse?.response ||
        "I apologize, but I could not generate a response at this time.";

      const words = aiResponse.split(" ");
      let typedResponse = "";

      for (let i = 0; i < words.length; i++) {
        if (!isGenerating.value) {
          break;
        }
        typedResponse += words[i] + (i < words.length - 1 ? " " : "");
        updateCurrentResponse({
          answer: typedResponse,
          sources: [],
          version: fullResponse?.version || "1.0.0",
          suggestions: null,
          suggestionType: null,
          role_name: null,
          reasoning,
          responsibility_name: null,
          actions: [],
        });
        const wordsPerBatch = Math.random() < 0.3 ? 4 : 3;
        if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50 + Math.random() * 100));
        }
      }

      let completedSuccessfully = false;
      if (isGenerating.value) {
        updateCurrentResponse({
          answer: aiResponse,
          sources: fullResponse?.sources || [],
          version: fullResponse?.version || "1.0.0",
          suggestions: null,
          suggestionType: null,
          role_name: fullResponse?.role_name || null,
          reasoning,
          responsibility_name: fullResponse?.responsibility_name || null,
          actions: [],
        });
        completedSuccessfully = true;
      }
      stopGenerating();

      if (typeof onAfterAskSuccess === "function" && completedSuccessfully) {
        Promise.resolve(onAfterAskSuccess()).catch((err) =>
          console.warn("Failed to refresh IQ conversations", err)
        );
      }

      return { conversationId: resolvedConversationId };
    } catch (error) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        isLoading.value = false;
        isGenerating.value = false;
        isThinking.value = false;
        currentReasoning.value = "";
        return { conversationId };
      }

      console.error("Error generating Ask AI attachment response:", error);
      isLoading.value = false;
      isGenerating.value = false;
      isThinking.value = false;
      currentReasoning.value = "";
      handleAskAiConversationFailure(error, rollbackUserMessageId);
      return { conversationId, failed: true };
    }
  };

  const generateAIResponse = async (
    userMessage,
    k = 5,
    customEndpoint = null,
    customPayload = null,
    onAfterAskSuccess = null,
    conversationIdForAsk = null,
    options = null
  ) => {
    const isSuggestionRequest = !!customPayload;
    // ER-12337: a caller may supply its own signal so this request is owned by a
    // durable background run instead of the shared IQ chat controller.
    const externalSignal = options && options.signal ? options.signal : null;

    const endSuggestionStreamIfNeeded = () => {
      if (isSuggestionRequest) {
        isSuggestionStreamActive.value = false;
      }
    };

    try {
      // Start with loading state and thinking animation
      isLoading.value = true;
      isGenerating.value = false;
      isThinking.value = true;
      currentResponse.value = "";
      currentReasoning.value = "";

      if (isSuggestionRequest) {
        isSuggestionStreamActive.value = true;
      }

      // Create abort controller for this request. Background runs supply their own
      // signal and must not overwrite the shared controller (ER-12337).
      let requestSignal = externalSignal;
      if (!requestSignal) {
        abortController = new AbortController();
        requestSignal = abortController.signal;
      }

      let streamingStarted = false;
      let fullResponse = null;
      let resolvedConversationId = conversationIdForAsk;

      // Try to use streaming API first
      try {
        const response = await askIQStream({
          question: userMessage,
          k: k,
          signal: requestSignal,
          customEndpoint: customEndpoint,
          customPayload: customPayload,
          conversationId: conversationIdForAsk,
          mentions: options?.mentions,
          onThinkingUpdate: (thinkingText) => {
            // Update reasoning in real-time as it streams from backend
            if (isThinking.value) {
              streamingStarted = true;
              currentReasoning.value = thinkingText;
            }
          },
          onComplete: (completeData) => {
            fullResponse = completeData;
            endSuggestionStreamIfNeeded();
          },
          onError: () => {
            endSuggestionStreamIfNeeded();
          },
        });

        // If streaming didn't provide data, use the final response
        if (!fullResponse && response.data) {
          fullResponse = response.data;
        }
        endSuggestionStreamIfNeeded();
      } catch (streamError) {
        // If streaming fails, fall back to regular API
        if (streamError.name === "AbortError" || streamError.name === "CanceledError") {
          throw streamError;
        }

        console.warn("Streaming failed, falling back to regular API:", streamError);

        // Show fallback reasoning while waiting
        if (!streamingStarted) {
          const fallbackReasoning = generateFallbackReasoning();
          const reasoningChars = fallbackReasoning.split("");
          let typedReasoning = "";
          const chunkSize = 3;

          for (let i = 0; i < reasoningChars.length; i += chunkSize) {
            if (!isThinking.value) break;
            typedReasoning += reasoningChars.slice(i, i + chunkSize).join("");
            currentReasoning.value = typedReasoning;
            await new Promise((resolve) => setTimeout(resolve, 20 + Math.random() * 30));
          }
        }

        // Fall back to regular API
        const response = await askIQ({
          question: userMessage,
          k: k,
          signal: requestSignal,
          customEndpoint: customEndpoint,
          customPayload: customPayload,
          conversationId: conversationIdForAsk,
          mentions: options?.mentions,
        });

        fullResponse = response.data;
        endSuggestionStreamIfNeeded();
      }

      if (!isLoading.value) return; // Stop if user clicked stop during API call

      // Get the complete response from API
      fullResponse = fullResponse || {};

      if (!customEndpoint && !customPayload && fullResponse.conversation_id) {
        resolvedConversationId = fullResponse.conversation_id;
      }

      // Use the accumulated reasoning from streaming or the response reasoning
      const reasoning = currentReasoning.value || fullResponse.reasoning || "";

      // Brief pause after reasoning is complete
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Thinking phase complete, switch to generating state
      isThinking.value = false;
      isLoading.value = false;
      isGenerating.value = true;

      // Determine suggestion type from the payload
      const suggestionType = customPayload?.suggestion_type || null;

      // Check if this is a suggestions response
      let aiResponse = "";
      let suggestions = null;
      let actions = [];
      let skipTypingEffect = false;
      const isVideoInstructionsRequest =
        !!customPayload &&
        typeof customPayload === "object" &&
        Object.prototype.hasOwnProperty.call(customPayload, "video_url");
      const useMDP452VideoParsing = isIdeaMDP452Enabled() && isVideoInstructionsRequest;

      if (fullResponse.suggestions && Array.isArray(fullResponse.suggestions)) {
        const suggestionCount = fullResponse.suggestions.length;

        // Check if suggestions is empty
        if (suggestionCount === 0) {
          if (suggestionType === "responsibility_checkpoint_criteria") {
            aiResponse =
              "I could not find any new checkpoint suggestions. Try providing more specific context.";
          } else {
            aiResponse =
              "I could not find any new Responsibility suggestions for this role. The role may already have comprehensive responsibilities attached, or try providing more specific context.";
          }
        } else {
          // Use message from API response if available, otherwise fallback
          if (fullResponse.message) {
            aiResponse = fullResponse.message;
          } else {
            // Fallback message based on suggestion type
            if (suggestionType === "responsibility_checkpoint_criteria") {
              aiResponse = `I suggested ${suggestionCount} new ${suggestionCount === 1 ? "checkpoint" : "checkpoints"} that you can review in the right pane, one checkpoint at a time.`;
            } else {
              aiResponse = `I suggested ${suggestionCount} new ${suggestionCount === 1 ? "Responsibility" : "Responsibilities"} that you can review, one Responsibility at a time:`;
            }
          }

          suggestions = fullResponse.suggestions;

          // Add action button - type differs based on suggestion type
          actions = [
            {
              label: "Refresh Suggestions",
              type:
                suggestionType === "responsibility_checkpoint_criteria"
                  ? "refresh-checkpoint-suggestions"
                  : "review-suggestions",
            },
          ];
        }
      } else {
        if (useMDP452VideoParsing) {
          const normalizedVideoResponse = normalizeVideoInstructionResponse(fullResponse);
          if (normalizedVideoResponse.type === "pass" || normalizedVideoResponse.type === "fail") {
            aiResponse = normalizedVideoResponse.answer || "";
            suggestions = normalizedVideoResponse.suggestions;
            // For the flagged mini flow, avoid post-thinking typing delay.
            skipTypingEffect = true;
          }
        }

        if (
          !aiResponse &&
          fullResponse.instructions &&
          fullResponse.instructions.steps &&
          Array.isArray(fullResponse.instructions.steps)
        ) {
          // Legacy video instructions response (validation PASS)
          const stepCount = fullResponse.instructions.steps.length;
          aiResponse =
            fullResponse.message ||
            `I analyzed the video and created ${stepCount} inspection ${stepCount === 1 ? "step" : "steps"}. Review them in the right pane.`;

          const mappedSteps = fullResponse.instructions.steps.map((step) => ({
            competence_category: normalizeCompetenceCategory(step.competence_category),
            inspector_action: step.inspector_action || "",
            look_for: step.look_for || "",
            pass_criteria: step.pass_criteria || "",
            fail_criteria: step.fail_criteria || "",
            timestamp_ref: step.timestamp_ref || "",
            video_jump_url: step.video_jump_url || "",
          }));

          suggestions = {
            steps: mappedSteps,
            summary: fullResponse.instructions.overall_rubric || null,
            error: null,
            video_url: fullResponse.video_url || null,
            video_title: fullResponse.instructions.video_title || null,
            validation:
              fullResponse.inspection_instructions?.validation || fullResponse.validation || null,
          };
        } else if (
          !aiResponse &&
          fullResponse.validation &&
          fullResponse.validation.status === "FAIL"
        ) {
          // Legacy validation-fail response
          aiResponse =
            fullResponse.validation.reasoning ||
            "This video does not match the context of the checkpoint!";
          suggestions = {
            steps: [],
            summary: null,
            error: fullResponse.validation.reasoning || "Video validation failed.",
            video_url: fullResponse.video_url || null,
          };
        } else if (!aiResponse && fullResponse.error) {
          // Video is not valid for context
          aiResponse = fullResponse.error;
        } else if (fullResponse.error) {
          aiResponse = fullResponse.error;
        } else if (!aiResponse) {
          aiResponse =
            fullResponse.answer ||
            fullResponse.response ||
            "I apologize, but I could not generate a response at this time.";
        }
      }

      if (!skipTypingEffect) {
        // Simulate typing effect for non-video payloads (ChatGPT-like behavior - word by word)
        const words = aiResponse.split(" ");
        let typedResponse = "";

        for (let i = 0; i < words.length; i++) {
          if (!isGenerating.value) break; // Stop if user clicked stop

          // Add word with space (except for the last word)
          typedResponse += words[i] + (i < words.length - 1 ? " " : "");

          // During typing, only show the answer text (no sources/actions yet)
          updateCurrentResponse({
            answer: typedResponse,
            sources: [], // No sources during typing
            version: fullResponse.version || "1.0.0",
            suggestions: null,
            suggestionType: null,
            role_name: null,
            reasoning: reasoning, // Keep reasoning visible during typing
            responsibility_name: null,
            actions: [], // No actions during typing
          });

          // ChatGPT-like speed: 3-4 words at a time with variable delay
          const wordsPerBatch = Math.random() < 0.3 ? 4 : 3; // 30% chance for 4 words, 70% for 3 words
          if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 50 + Math.random() * 100));
          }
        }
      }

      // After typing is complete, show the full response with sources, actions, and suggestions
      let completedSuccessfully = false;
      if (isGenerating.value) {
        const finalResponse = {
          answer: aiResponse,
          sources: fullResponse.sources || [],
          version: fullResponse.version || "1.0.0",
          suggestions: suggestions,
          suggestionType: suggestionType,
          role_name: fullResponse.role_name || null,
          reasoning: reasoning,
          responsibility_name: fullResponse.responsibility_name || null,
          actions: actions,
        };
        updateCurrentResponse(finalResponse);
        completedSuccessfully = true;
      }
      if (externalSignal) {
        finishExternalGeneration();
      } else {
        stopGenerating();
      }

      if (
        !customEndpoint &&
        !customPayload &&
        typeof onAfterAskSuccess === "function" &&
        completedSuccessfully
      ) {
        Promise.resolve(onAfterAskSuccess()).catch((err) =>
          console.warn("Failed to refresh IQ conversations", err)
        );
      }

      return { conversationId: resolvedConversationId };
    } catch (error) {
      // Don't show error if request was intentionally aborted
      if (error.name === "AbortError" || error.name === "CanceledError") {
        console.log("Request cancelled by user");
        isLoading.value = false;
        isGenerating.value = false;
        isThinking.value = false;
        isSuggestionStreamActive.value = false;
        currentReasoning.value = "";
        return;
      }

      console.error("Error generating AI response:", error);
      isLoading.value = false;
      isGenerating.value = false;
      isThinking.value = false;
      isSuggestionStreamActive.value = false;
      currentReasoning.value = "";

      // Add error message
      const errorMessage =
        "I apologize, but I encountered an error while processing your request. Please try again.";
      addAIMessage(errorMessage, { isError: true });
    }
  };

  const generateCustomResponse = async (customResponseText, actions = []) => {
    try {
      // Start with loading state (thinking period)
      isLoading.value = true;
      isGenerating.value = false;
      currentResponse.value = "";

      // Wait 1-2 seconds before starting to type (thinking time)
      await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 1000));

      if (!isLoading.value) return; // Stop if user clicked stop during thinking

      // Switch to generating state
      isLoading.value = false;
      isGenerating.value = true;

      // Simulate typing effect (ChatGPT-like behavior - word by word)
      const words = customResponseText.split(" ");
      let typedResponse = "";

      for (let i = 0; i < words.length; i++) {
        if (!isGenerating.value) break; // Stop if user clicked stop

        // Add word with space (except for the last word)
        typedResponse += words[i] + (i < words.length - 1 ? " " : "");

        // During typing, only show the answer text (no actions yet)
        updateCurrentResponse({
          answer: typedResponse,
          sources: [],
          actions: [],
          version: "1.0.0",
        });

        // ChatGPT-like speed: 3-4 words at a time with variable delay
        const wordsPerBatch = Math.random() < 0.3 ? 4 : 3;
        if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50 + Math.random() * 100));
        }
      }

      // After typing is complete, show actions
      if (isGenerating.value && actions.length > 0) {
        updateCurrentResponse({
          answer: customResponseText,
          sources: [],
          actions: actions,
          version: "1.0.0",
        });
      }

      stopGenerating();
    } catch (error) {
      console.error("Error generating custom response:", error);
      isLoading.value = false;
      isGenerating.value = false;

      // Add error message
      const errorMessage = "I apologize, but I encountered an error while processing your request.";
      addAIMessage(errorMessage);
    }
  };

  return {
    // State
    messages,
    isLoading,
    isGenerating,
    isThinking,
    isSuggestionStreamActive,
    currentResponse,
    currentReasoning,

    // Getters
    hasMessages,
    lastMessage,

    // Actions
    addUserMessage,
    rollbackUserMessage,
    handleAskAiConversationFailure,
    addAIMessage,
    startGenerating,
    stopGenerating,
    startThinking,
    stopThinking,
    updateCurrentResponse,
    updateCurrentReasoning,
    clearChat,
    setMessages,
    generateRandomResponse,
    generateAIResponse,
    generateAttachmentResponse,
    generateCustomResponse,
  };
});
